import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { showToast } from './toast';

// Supported export formats
export type ExportFormat = 'csv' | 'json' | 'xlsx';

export interface ExportOptions {
  filename?: string;
  format?: ExportFormat;
  includeHeaders?: boolean;
  dateFormat?: string;
  transformData?: (data: any[]) => any[];
}

// Export data to various formats
export class DataExporter {
  static async exportData<T extends Record<string, any>>(
    data: T[],
    options: ExportOptions = {}
  ): Promise<void> {
    const {
      filename = 'export',
      format = 'csv',
      includeHeaders = true,
      dateFormat = 'YYYY-MM-DD HH:mm:ss',
      transformData,
    } = options;

    if (!data || data.length === 0) {
      showToast.warning('No data to export');
      return;
    }

    try {
      // Transform data if needed
      let exportData = transformData ? transformData(data) : data;

      // Format dates and handle special data types
      exportData = this.formatExportData(exportData, dateFormat);

      // Generate file based on format
      switch (format) {
        case 'csv':
          await this.exportToCSV(exportData, filename, includeHeaders);
          break;
        case 'json':
          await this.exportToJSON(exportData, filename);
          break;
        case 'xlsx':
          await this.exportToExcel(exportData, filename);
          break;
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      showToast.success(`Data exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      showToast.error('Failed to export data');
      throw error;
    }
  }

  private static formatExportData(data: any[], dateFormat: string): any[] {
    return data.map(item => {
      const formatted: any = {};

      for (const [key, value] of Object.entries(item)) {
        if (value instanceof Date) {
          formatted[key] = this.formatDate(value, dateFormat);
        } else if (typeof value === 'boolean') {
          formatted[key] = value ? 'Yes' : 'No';
        } else if (value === null || value === undefined) {
          formatted[key] = '';
        } else if (typeof value === 'object') {
          formatted[key] = JSON.stringify(value);
        } else {
          formatted[key] = value;
        }
      }

      return formatted;
    });
  }

  private static formatDate(date: Date, format: string): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return format
      .replace('YYYY', year.toString())
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds);
  }

  private static async exportToCSV(
    data: any[],
    filename: string,
    includeHeaders: boolean
  ): Promise<void> {
    const csv = Papa.unparse(data, {
      header: includeHeaders,
      skipEmptyLines: true,
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}.csv`);
  }

  private static async exportToJSON(data: any[], filename: string): Promise<void> {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    saveAs(blob, `${filename}.json`);
  }

  private static async exportToExcel(data: any[], filename: string): Promise<void> {
    // For Excel export, we'll use a simple CSV approach since we don't have a heavy Excel library
    // In a real implementation, you'd use a library like xlsx or exceljs
    await this.exportToCSV(data, filename, true);

    // Note: For full Excel support, you'd need to add:
    // pnpm install xlsx
    // Then implement proper Excel export with multiple sheets, formatting, etc.
  }
}

// Data import functionality
export class DataImporter {
  static async importFromFile(file: File): Promise<any[]> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'csv':
        return this.importFromCSV(file);
      case 'json':
        return this.importFromJSON(file);
      case 'xlsx':
      case 'xls':
        return this.importFromExcel(file);
      default:
        throw new Error(`Unsupported file format: ${fileExtension}`);
    }
  }

  private static async importFromCSV(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: any) => {
          if (results.errors.length > 0) {
            console.warn('CSV parsing errors:', results.errors);
          }
          resolve(results.data);
        },
        error: (error: any) => {
          reject(new Error(`CSV parsing failed: ${error.message}`));
        },
      });
    });
  }

  private static async importFromJSON(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          // Ensure we return an array
          resolve(Array.isArray(data) ? data : [data]);
        } catch (error) {
          reject(new Error('Invalid JSON format'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  private static async importFromExcel(file: File): Promise<any[]> {
    // For Excel import, you'd need a library like xlsx
    // For now, we'll throw an error
    throw new Error('Excel import not implemented. Please use CSV or JSON format.');
  }
}

// Specialized exporters for different data types
export class UserExporter {
  static async exportUsers(users: any[], format: ExportFormat = 'csv'): Promise<void> {
    const exportData = users.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role?.name || user.role,
      status: user.status,
      joinedAt: user.createdAt || user.joinedAt,
      lastActive: user.lastActive || user.updatedAt,
      phone: user.phone,
      profilePictureUrl: user.profilePictureUrl,
    }));

    await DataExporter.exportData(exportData, {
      filename: `users_${new Date().toISOString().split('T')[0]}`,
      format,
      transformData: (data) => data.map(user => ({
        ...user,
        joinedAt: new Date(user.joinedAt),
        lastActive: new Date(user.lastActive),
      })),
    });
  }
}

export class StreamExporter {
  static async exportStreams(streams: any[], format: ExportFormat = 'csv'): Promise<void> {
    const exportData = streams.map(stream => ({
      id: stream.id,
      title: stream.title,
      description: stream.description,
      status: stream.status,
      creator: stream.creator?.name || stream.createdBy,
      createdAt: stream.createdAt,
      scheduledStart: stream.scheduledStart,
      actualStart: stream.actualStart,
      duration: stream.duration,
      viewerCount: stream.viewerCount,
      isPrivate: stream.isPrivate,
      tags: stream.tags?.join(', ') || '',
    }));

    await DataExporter.exportData(exportData, {
      filename: `streams_${new Date().toISOString().split('T')[0]}`,
      format,
      transformData: (data) => data.map(stream => ({
        ...stream,
        createdAt: new Date(stream.createdAt),
        scheduledStart: stream.scheduledStart ? new Date(stream.scheduledStart) : null,
        actualStart: stream.actualStart ? new Date(stream.actualStart) : null,
      })),
    });
  }
}

export class CommentExporter {
  static async exportComments(comments: any[], format: ExportFormat = 'csv'): Promise<void> {
    const exportData = comments.map(comment => ({
      id: comment.id,
      streamId: comment.streamId,
      content: comment.content,
      author: comment.user?.name || comment.userId,
      createdAt: comment.createdAt,
      updatedAt: comment.updatedAt,
      moderated: comment.moderated,
      moderationReason: comment.moderationReason,
      likes: comment.likes,
      replyCount: comment.replyCount,
      parentId: comment.parentId,
    }));

    await DataExporter.exportData(exportData, {
      filename: `comments_${new Date().toISOString().split('T')[0]}`,
      format,
      transformData: (data) => data.map(comment => ({
        ...comment,
        createdAt: new Date(comment.createdAt),
        updatedAt: new Date(comment.updatedAt),
      })),
    });
  }
}

// Analytics export
export class AnalyticsExporter {
  static async exportAnalytics(events: any[], format: ExportFormat = 'json'): Promise<void> {
    await DataExporter.exportData(events, {
      filename: `analytics_${new Date().toISOString().split('T')[0]}`,
      format,
      transformData: (data) => data.map(event => ({
        ...event,
        timestamp: new Date(event.timestamp),
      })),
    });
  }
}

// Generic data validation for imports
export class DataValidator {
  static validateImportedData<T>(
    data: any[],
    schema: Record<string, {
      type: 'string' | 'number' | 'boolean' | 'date';
      required?: boolean;
      minLength?: number;
      maxLength?: number;
      pattern?: RegExp;
      enum?: any[];
    }>
  ): { valid: T[]; errors: string[] } {
    const valid: T[] = [];
    const errors: string[] = [];

    data.forEach((item, index) => {
      const rowNumber = index + 1;
      let isValid = true;

      for (const [field, rules] of Object.entries(schema)) {
        const value = item[field];

        // Check required fields
        if (rules.required && (value === undefined || value === null || value === '')) {
          errors.push(`Row ${rowNumber}: ${field} is required`);
          isValid = false;
          continue;
        }

        if (value === undefined || value === null || value === '') {
          continue; // Skip validation for optional empty fields
        }

        // Type validation
        switch (rules.type) {
          case 'string':
            if (typeof value !== 'string') {
              errors.push(`Row ${rowNumber}: ${field} must be a string`);
              isValid = false;
            } else {
              if (rules.minLength && value.length < rules.minLength) {
                errors.push(`Row ${rowNumber}: ${field} must be at least ${rules.minLength} characters`);
                isValid = false;
              }
              if (rules.maxLength && value.length > rules.maxLength) {
                errors.push(`Row ${rowNumber}: ${field} must be at most ${rules.maxLength} characters`);
                isValid = false;
              }
              if (rules.pattern && !rules.pattern.test(value)) {
                errors.push(`Row ${rowNumber}: ${field} format is invalid`);
                isValid = false;
              }
            }
            break;

          case 'number':
            if (typeof value !== 'number' && isNaN(Number(value))) {
              errors.push(`Row ${rowNumber}: ${field} must be a number`);
              isValid = false;
            }
            break;

          case 'boolean':
            if (typeof value !== 'boolean' && !['true', 'false', '1', '0', 'yes', 'no'].includes(String(value).toLowerCase())) {
              errors.push(`Row ${rowNumber}: ${field} must be a boolean`);
              isValid = false;
            }
            break;

          case 'date':
            if (isNaN(Date.parse(value))) {
              errors.push(`Row ${rowNumber}: ${field} must be a valid date`);
              isValid = false;
            }
            break;
        }

        // Enum validation
        if (rules.enum && !rules.enum.includes(value)) {
          errors.push(`Row ${rowNumber}: ${field} must be one of: ${rules.enum.join(', ')}`);
          isValid = false;
        }
      }

      if (isValid) {
        valid.push(item as T);
      }
    });

    return { valid, errors };
  }
}
